class RateLimitNode<T> {
    value: T;
    next?: RateLimitNode<T>;

    constructor(value: T) {
        this.value = value;
    }
}

class RateLimitQueue<T> {
    private head?: RateLimitNode<T>;
    private tail?: RateLimitNode<T>;
    size: number = 0;

    constructor() {
        this.clear();
    }

    enqueue(value: T): void {
        const node = new RateLimitNode(value);

        if (this.head && this.tail) {
            this.tail.next = node;
            this.tail = node;
        } else {
            this.head = node;
            this.tail = node;
        }

        this.size++;
    }

    dequeue(): T | undefined {
        const current = this.head;
        if (!current) {
            return;
        }

        this.head = current.next;
        if (!this.head) {
            this.tail = undefined;
        }
        this.size--;
        return current.value;
    }

    clear(): void {
        this.head = undefined;
        this.tail = undefined;
        this.size = 0;
    }

    *[Symbol.iterator](): IterableIterator<T> {
        let current = this.head;

        while (current) {
            yield current.value;
            current = current.next;
        }
    }
}

interface RateLimitOptions {
    /** Minimum delay between consecutive dispatches, in milliseconds. */
    dispatchIntervalMs?: number;
    /**
     * When `true`, the limiter runs in batch mode: up to `concurrency` tasks
     * are dispatched together, and the next group is only released once every
     * task in the current group has settled (resolved or rejected).
     *
     * When `false` (default), the limiter behaves as a sliding window — a
     * queued task starts as soon as any in-flight task finishes.
     */
    batchMode?: boolean;
}

interface RateLimitWrapOptions extends RateLimitOptions {
    concurrency: number;
}

class RateLimit {
    private readonly queue = new RateLimitQueue<() => void>();
    private _activeCount = 0;
    private _concurrency: number;
    private readonly dispatchIntervalMs: number;
    private readonly batchMode: boolean;
    private nextDispatchTime = 0;

    constructor(concurrency: number, options: RateLimitOptions = {}) {
        RateLimit.validateConcurrency(concurrency);
        this._concurrency = concurrency;
        this.dispatchIntervalMs = Math.max(0, options.dispatchIntervalMs ?? 0);
        this.batchMode = options.batchMode ?? false;
    }

    static validateConcurrency(concurrency: unknown): asserts concurrency is number {
        if (
            !(
                (Number.isInteger(concurrency) || concurrency === Number.POSITIVE_INFINITY) &&
                (concurrency as number) > 0
            )
        ) {
            throw new TypeError("Expected `concurrency` to be a number from 1 and up");
        }
    }

    static wrap<Arguments extends unknown[], ReturnType_>(
        function_: (...arguments_: Arguments) => PromiseLike<ReturnType_> | ReturnType_,
        options: RateLimitWrapOptions,
    ): (...arguments_: Arguments) => Promise<ReturnType_> {
        const limit = new RateLimit(options.concurrency, {
            dispatchIntervalMs: options.dispatchIntervalMs,
            batchMode: options.batchMode,
        });
        return (...arguments_: Arguments) => limit.run(function_, ...arguments_);
    }

    get activeCount(): number {
        return this._activeCount;
    }

    get pendingCount(): number {
        return this.queue.size;
    }

    get concurrency(): number {
        return this._concurrency;
    }

    set concurrency(newConcurrency: number) {
        RateLimit.validateConcurrency(newConcurrency);
        this._concurrency = newConcurrency;

        queueMicrotask(() => {
            // In batch mode, do not expand a batch that is already running —
            // the new limit takes effect when the next batch is dispatched.
            if (this.batchMode && this._activeCount > 0) {
                return;
            }
            while (this._activeCount < this._concurrency && this.queue.size > 0) {
                this.resumeNext();
            }
        });
    }

    clearQueue(): void {
        this.queue.clear();
    }

    run<Arguments extends unknown[], ReturnType_>(
        function_: (...arguments_: Arguments) => PromiseLike<ReturnType_> | ReturnType_,
        ...arguments_: Arguments
    ): Promise<ReturnType_> {
        return new Promise<ReturnType_>((resolve) => {
            this.enqueueTask(function_, resolve, arguments_);
        });
    }

    async map<T, R>(
        array: readonly T[],
        function_: (value: T, index: number) => PromiseLike<R> | R,
    ): Promise<R[]> {
        const promises = array.map((value, index) => this.run(function_, value, index));
        return Promise.all(promises);
    }

    private resumeNext(): void {
        if (this._activeCount >= this._concurrency) {
            return;
        }

        const fn = this.queue.dequeue();
        if (!fn) {
            return;
        }

        this._activeCount++;

        if (this.dispatchIntervalMs <= 0) {
            fn();
            return;
        }

        // Space consecutive dispatches so the backend isn't hit with a burst.
        const now = Date.now();
        const dispatchAt = Math.max(now, this.nextDispatchTime);
        this.nextDispatchTime = dispatchAt + this.dispatchIntervalMs;
        const wait = dispatchAt - now;

        if (wait > 0) {
            setTimeout(fn, wait);
        } else {
            fn();
        }
    }

    private next(): void {
        this._activeCount--;

        if (this.batchMode) {
            // Hold dispatch until the current batch has fully drained, then
            // release up to `concurrency` queued tasks together as the next
            // batch.
            if (this._activeCount === 0 && this.queue.size > 0) {
                const batchSize = Math.min(this._concurrency, this.queue.size);
                for (let i = 0; i < batchSize; i++) {
                    this.resumeNext();
                }
            }
            return;
        }

        this.resumeNext();
    }

    private async executeTask<Arguments extends unknown[], ReturnType_>(
        function_: (...arguments_: Arguments) => PromiseLike<ReturnType_> | ReturnType_,
        resolve: (value: PromiseLike<ReturnType_> | ReturnType_) => void,
        arguments_: Arguments,
    ): Promise<void> {
        const result = (async () => function_(...arguments_))();
        resolve(result);

        try {
            await result;
        } catch (error) {
            console.error("RateLimit run error", error);
        }

        this.next();
    }

    private enqueueTask<Arguments extends unknown[], ReturnType_>(
        function_: (...arguments_: Arguments) => PromiseLike<ReturnType_> | ReturnType_,
        resolve: (value: PromiseLike<ReturnType_> | ReturnType_) => void,
        arguments_: Arguments,
    ): void {
        new Promise<void>((internalResolve) => {
            this.queue.enqueue(() => internalResolve());
        }).then(() => this.executeTask(function_, resolve, arguments_));

        // The same gate works for both modes:
        //  - sliding window: keeps the window saturated up to concurrency
        //  - batch mode: fills the first/current batch up to concurrency, after
        //    which next() takes over and waits for a full drain
        if (this._activeCount < this._concurrency) {
            this.resumeNext();
        }
    }
}

export { RateLimit, RateLimitNode, RateLimitQueue };
export type { RateLimitOptions, RateLimitWrapOptions };
